import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, History, Paperclip, Pause, Pencil, Play } from 'lucide-react';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStartTimer } from '../home/useHomeData';
import { runningWorkTimerFor } from '../home/homeModel';
import { useStopTimer } from '../timer/useStopTimer';
import { canRowStartTimer, canUseTimer } from '../timer/timerModel';
import TaskFormModal from '../projects/TaskFormModal';
import type { Column, RowAction } from '../DataTable';
import { useAttachFiles } from './TaskFiles';
import { CancelTicketDialog } from '../../pages/TicketDetailPage';
import { undoableTicketStatus } from '../../utils/undoableTicketStatus';
import type { MilestoneStatus, Ticket } from '../../types';
import FinishTaskDialog from './FinishTaskDialog';
import TimerSummaryModal, { timerTargetFromTimer } from '../TimerSummaryModal';
import { usePermissions } from '../../hooks/usePermissions';
import TaskStatusPill from './TaskStatusPill';
import { canChangeTaskStatus, canUseTaskStatusPill, needsFinishWindow } from './taskStatusChangeModel';

/** The fields a task row needs for its actions. */
export interface TaskRowLike {
  id: string;
  title: string;
  status: string;
  cancelled_at?: string | null;
  agents?: { id: string }[] | null;
  assigned_agent_id?: string | null;
}

/**
 * P-06/E (D-62, D-44, T-17, T-19) — the SAME ▶ and ⋯ on every task row:
 * "כל המשימות" and the "משימות" tabs of a project, a milestone, an engagement
 * and a customer.
 *
 *   ▶          start work (the one start flow, D-63: `useStartTimer`)
 *   ⋯ 📎       attach files (several, from the computer)
 *   ⋯          all the task's logs (/work-logs?ticket=<id>)
 *   ⋯          edit (the one task form)
 *   ⋯          cancel (with a reason)
 *
 * `extra(task)` adds a page's own items after these (e.g. "רישום שעות", delete).
 * Render `dialogs` once next to the table.
 */
export function useTaskRowActions(opts: {
  onChanged?: () => void;
  extra?: (task: TaskRowLike) => RowAction[];
  /** ▶ is the table's first column (`startColumn`); the row action then serves touch only. */
  startInColumn?: boolean;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isStaff = user?.role === 'admin' || user?.role === 'agent';
  // D-263 round 2: the status pill follows the server's rule (any OTE employee), not the login claim.
  const { isOteEmployee } = usePermissions();
  const canStatus = canUseTaskStatusPill(user?.role, isOteEmployee);
  // FIX-OPENREC — ▶ follows the server's timer rule (any OTE employee), not the login claim.
  const canTimer = canUseTimer(user?.role, isOteEmployee);
  const startTimer = useStartTimer({ onStarted: () => opts.onChanged?.() });
  // D-332 - a task whose timer runs shows pause + finish instead of start (shared ['timer-status'], same as Home).
  const stopTimer = useStopTimer({ onStopped: () => opts.onChanged?.() });
  const { data: timerData } = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: canTimer,
    refetchInterval: 30_000,
    staleTime: 0,
  });
  const runningOf = (taskId: string) => runningWorkTimerFor(timerData?.data?.timers, taskId);
  const attach = useAttachFiles(() => opts.onChanged?.());
  const [editing, setEditing] = useState<TaskRowLike | null>(null);
  const [cancelling, setCancelling] = useState<TaskRowLike | null>(null);
  // D-263 - "הושלם" on a task with MY running timer opens the finish window (stops the timer) first.
  const [finishing, setFinishing] = useState<TaskRowLike | null>(null);
  // D-332 - "סיום ורישום" of a running task = the same TimerSummaryModal as "המיקוד שלי" (logs the work, does not close the task).
  const [summarizing, setSummarizing] = useState<ReturnType<typeof runningOf>>(null);
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: (v: { id: string; reason: string }) => api.cancelTicket(v.id, v.reason),
    onSuccess: () => { setCancelling(null); opts.onChanged?.(); },
  });

  /** D-263 - a pick in the status pill of a row: saved at once (the one P-29 route, with "בטל"). */
  const changeStatus = async (task: TaskRowLike, next: MilestoneStatus) => {
    if (next === 'completed') {
      try {
        const res = await queryClient.fetchQuery({ queryKey: ['timer-status'], queryFn: () => api.getTimerStatus(), staleTime: 0 });
        if (needsFinishWindow(next, res?.data?.timers, task.id)) { setFinishing(task); return; }
      } catch { /* advisory pre-check only: no timer info -> the plain status change */ }
    }
    undoableTicketStatus({ qc: queryClient, id: task.id, status: next, onCommitted: () => opts.onChanged?.() });
  };
  /** D-263 - the status cell of a task row: a pill with a list for staff (not for a completed / cancelled task), else the badge. */
  const statusCell = (task: TaskRowLike) => (
    <TaskStatusPill
      taskId={task.id}
      status={task.status}
      title={task.title}
      onChange={canChangeTaskStatus({ isStaff: canStatus, status: task.status, cancelled_at: task.cancelled_at }) ? (s) => { void changeStatus(task, s); } : undefined}
    />
  );

  const rowActions = (task: TaskRowLike): RowAction[] => {
    if (!isStaff && !canTimer) return [];
    const actions: RowAction[] = [];
    // FU-28 (א2 / D-67) — ▶ only on a task the user is on (canStartTimerOn).
    const runningTm = canTimer ? runningOf(task.id) : null;
    if (runningTm) {
      actions.push({
        key: 'timer',
        label: t('timer.pauseFor', { title: task.title }),
        icon: <Pause size={16} strokeWidth={1.75} />,
        inline: !opts.startInColumn,
        touchVisible: true,
        // D-332: the pause is in the desktop ⋯ too (the start stays touch-only when the column carries it).
        disabled: stopTimer.isPending,
        onSelect: () => stopTimer.mutate(runningTm),
      });
      actions.push({
        key: 'timer-finish',
        label: t('timer.finishFor', { title: task.title }),
        icon: <Check size={16} strokeWidth={1.75} />,
        onSelect: () => setSummarizing(runningTm),
      });
    } else if (canTimer && canRowStartTimer(task, user?.id)) {
      actions.push({
        key: 'timer',
        label: t('taskRow.start', { title: task.title }),
        icon: <Play size={16} strokeWidth={1.75} />,
        inline: !opts.startInColumn,
        touchVisible: true,
        ...(opts.startInColumn ? { touchOnly: true } : {}),
        disabled: startTimer.isPending,
        onSelect: () => startTimer.mutate({ id: task.id, title: task.title }),
      });
    }
    if (!isStaff) return actions;
    actions.push(
      { key: 'attach', label: t('taskRow.attach'), icon: <Paperclip size={16} strokeWidth={1.75} />, disabled: attach.busy, onSelect: () => attach.open(task.id) },
      { key: 'logs', label: t('taskRow.allLogs'), icon: <History size={16} strokeWidth={1.75} />, onSelect: () => navigate(`/work-logs?ticket=${task.id}`) },
      { key: 'edit', label: t('tasks.list.edit'), icon: <Pencil size={16} strokeWidth={1.75} />, disabled: !!task.cancelled_at, onSelect: () => setEditing(task) },
    );
    if (!task.cancelled_at) {
      actions.push({ key: 'cancel', label: t('ticketDetail.cancelTask'), icon: <Ban size={16} strokeWidth={1.75} />, onSelect: () => setCancelling(task) });
    }
    actions.push(...(opts.extra?.(task) ?? []));
    return actions;
  };

  const dialogs: ReactNode = (
    <>
      {attach.input}
      {editing && (
        <TaskFormModal
          mode="edit"
          task={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); opts.onChanged?.(); }}
        />
      )}
      {cancelling && (
        <CancelTicketDialog
          isTask
          pending={cancel.isPending}
          error={cancel.error}
          onConfirm={(reason) => cancel.mutate({ id: cancelling.id, reason })}
          onClose={() => setCancelling(null)}
        />
      )}
      {summarizing && (
        <TimerSummaryModal
          target={timerTargetFromTimer(summarizing)}
          onClose={() => setSummarizing(null)}
          onDone={() => {
            setSummarizing(null);
            queryClient.invalidateQueries({ queryKey: ['timer-status'] });
            queryClient.invalidateQueries({ queryKey: ['work-logs'] });
            queryClient.invalidateQueries({ queryKey: ['work-records'] });
            opts.onChanged?.();
          }}
        />
      )}
      {finishing && (
        <FinishTaskDialog
          task={finishing as unknown as Ticket}
          onClose={() => setFinishing(null)}
          onDone={() => {
            setFinishing(null);
            queryClient.invalidateQueries({ queryKey: ['timer-status'] });
            opts.onChanged?.();
          }}
        />
      )}
    </>
  );

  /** D-62 — "▶" as the first column of a task table (pointer devices; touch: the row's visible ▶). */
  const startColumn = (): Column<TaskRowLike & Record<string, any>> => ({
    key: 'start',
    header: t('taskRow.startHeader'),
    hideHeader: true,
    width: 'w-10',
    card: 'none',
    render: (task) => (canTimer && runningOf(task.id) ? (
      <span className="inline-flex items-center">
        <button
          type="button"
          className="dt-above inline-flex size-8 items-center justify-center rounded-lg text-accent-text transition-colors duration-micro hover:bg-surface-subtle"
          aria-label={t('timer.pauseFor', { title: task.title })}
          title={t('timer.pause')}
          disabled={stopTimer.isPending}
          onClick={(e) => { e.stopPropagation(); stopTimer.mutate(runningOf(task.id)!); }}
          data-testid="task-row-pause"
        >
          <Pause size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </span>
    ) : canTimer && canRowStartTimer(task, user?.id) ? (
      <button
        type="button"
        className="dt-above inline-flex size-8 items-center justify-center rounded-lg text-text-2 transition-colors duration-micro hover:bg-surface-subtle hover:text-text"
        aria-label={t('taskRow.start', { title: task.title })}
        title={t('taskRow.start', { title: task.title })}
        disabled={startTimer.isPending}
        onClick={(e) => { e.stopPropagation(); startTimer.mutate({ id: task.id, title: task.title }); }}
        data-testid="task-row-start"
      >
        <Play size={16} strokeWidth={1.75} aria-hidden="true" />
      </button>
    ) : null),
  });

  /** C-31 (clarity): the existing assign action is the task form; "+ שיוך" in an empty assignees cell opens it. */
  const openEdit = (task: TaskRowLike) => { if (isStaff && !task.cancelled_at) setEditing(task); };

  return { rowActions, dialogs, startColumn, statusCell, openEdit, canEdit: isStaff, runningOf, pauseTimer: (timer: Parameters<typeof stopTimer.mutate>[0]) => stopTimer.mutate(timer), pausing: stopTimer.isPending };
}
